import React, { useCallback, useMemo, useState } from "react";
import { usePlanGuard } from "../Subscription/planModal";

import RequestComposerDrawer from "./RequestComposerDrawer";

/**
 * Open the Insurance Verification request composer from anywhere.
 *
 * Requesting a breakdown used to work one way on the Insurance Verification page
 * and another way from the patient page and the insurance modal — and the second
 * way dispatched the agent WITHOUT opening an InsuranceVerificationRequest. That
 * is what made failures vanish: `request_outcomes.apply_agent_outcome` hands a
 * failed run to FTC only when it can find an open request behind it, so a run
 * started from the modal had nothing to fall back to and appeared nowhere. 307 of
 * 348 failed runs on production were invisible for exactly this reason.
 *
 * Every entry point now goes through one flow. Nothing is lost by doing so: the
 * composer's endpoint already splits the selected policies into agent-serviced
 * and manual, meters each correctly, opens the rows, and only then dispatches —
 * so a scraper carrier still fires immediately, it just leaves a record behind.
 *
 * Usage:
 *
 *     const { openComposer, composer } = useRequestComposer({ overModal: true });
 *     ...
 *     <Button onClick={() => openComposer(patient, { policyId, requestType })} />
 *     {composer}
 */

// Above the insurance modal, which sits at antd's auto ~1000 (InsuranceMasterModal
// deliberately sets no zIndex — its comment warns that hard-coding one breaks the
// modals nested inside its tabs).
const OVER_MODAL_Z = 1200;

export default function useRequestComposer({ overModal = false, onSubmitted } = {}) {
  const [seed, setSeed] = useState(null);
  // A request is paid work: no plan and no trial left opens the plans instead.
  const guardPlan = usePlanGuard();

  const openComposer = useCallback(function (patient, options) {
    if (!patient || !patient.id) return;
    if (!guardPlan()) return;
    const opts = options || {};
    setSeed({
      patient: patient,
      // A single policy is the common case (the row or modal the user is looking
      // at). `policyIds` is accepted too, for a caller that already has a set.
      policyIds: opts.policyIds
        || (opts.policyId != null ? [opts.policyId] : null),
      requestType: opts.requestType || null,   // unseeded: the user picks one
    });
  }, [guardPlan]);

  const close = useCallback(function () { setSeed(null); }, []);

  const composer = useMemo(
    function () {
      return (
        <RequestComposerDrawer
          // Remount per launch so the seed is applied cleanly. The page-level
          // instance stays mounted and resets itself on open; here the seed
          // changes between launches, so a fresh instance is simpler than
          // teaching the reset effect to diff it.
          key={seed ? `${seed.patient.id}:${seed.requestType}` : "closed"}
          open={!!seed}
          onClose={close}
          onSubmitted={function () { if (onSubmitted) onSubmitted(); }}
          initialPatient={seed ? seed.patient : null}
          initialPolicyIds={seed ? seed.policyIds : null}
          initialRequestType={seed ? seed.requestType : null}
          zIndex={overModal ? OVER_MODAL_Z : undefined}
        />
      );
    },
    [seed, close, onSubmitted, overModal],
  );

  return { openComposer, closeComposer: close, composer, composerOpen: !!seed };
}
